import type { BaseItemSchema } from "@memorang/types";

const key = {
	zero: 0,
	fifty: 50,
	hundred: 100,
};

export const confidenceRanges = {
	[key.zero]: [0, 49], // low = 0
	[key.fifty]: [50, 84], // medium = 1
	[key.hundred]: [85, 100], // high = 2
};

const calculateTextLength = (item: BaseItemSchema) => {
	const { content, currentStimulusItemIndex, stimulus } = item;
	const stem = content?.stem;
	const choices = item.response.type === "choice" ? item.response.choices : [];
	let numChars = 0;

	numChars += stem?.text?.length ?? 0;
	if (currentStimulusItemIndex === 1 && stimulus?.passage?.paragraphs) {
		numChars += stimulus?.passage?.paragraphs?.reduce(
			(acc, paragraph) => acc + paragraph.text.length,
			0,
		);
	}

	for (const item of choices) {
		numChars += item.text ? item.text.length : 0;
	}

	return numChars;
};

// Provides a factor (0-1) whether the user's answer was fast or slow
// Speed is another way to estimate how confident a user is
const calcTimeFactor = (
	timeTaken: number,
	timeExpected: number,
	scaleFactor = 20,
) => {
	if (timeTaken <= timeExpected) {
		return 1.0;
	}
	const diff = timeTaken - timeExpected;
	return Math.exp(-diff / scaleFactor);
};

/* -----------------Score functions--------------- */
type ScoreParamsMultiChoice = {
	confidence: number;
	currentScore: number;
	timeFactor: number;
	correct: boolean;
};

export const getNewScoreMultiChoice = (params: ScoreParamsMultiChoice) => {
	const { confidence, currentScore, timeFactor, correct } = params;
	let newScore = 0;
	if (correct) {
		// We get the baseline score to increase from
		const minScoreRange = confidenceRanges[Math.max(key.fifty, confidence)][0];

		const minScore = Math.max(currentScore, minScoreRange);

		// Calculate how much to increase the score
		const maxScoreRange = confidenceRanges[confidence][1];
		const diffIncrease = Math.max(maxScoreRange - minScore, 0);
		const increment = diffIncrease * timeFactor;
		newScore = Math.min(minScore + increment, 100);
	} else {
		newScore = 0;
	}
	return newScore;
};

export const normalizeScore = (score: number) =>
	Math.min(Math.round(Math.max(score, 0) * 100) / 100, 100);

/* -----------------Match Constants--------------- */
function estimateReadingTimeInSeconds(textLength: number) {
	const AVERAGE_READING_SPEED_WPS = 3.33;
	return textLength / AVERAGE_READING_SPEED_WPS;
}

function estimateImageTimeInSeconds(imageCount: number) {
	const AVERAGE_TIME_PER_IMAGE = 3; // 3 seconds per image
	return imageCount * AVERAGE_TIME_PER_IMAGE;
}

function estimateAudioTimeInSeconds(audioLengthInSeconds: number) {
	return audioLengthInSeconds;
}

export function calculateExpectedTime({
	textLength = 0,
	imageCount = 0,
	audioLengthInSeconds = 0,
	overheadBuffer = 2, // A small buffer (seconds) for overhead
}) {
	const readingTime = estimateReadingTimeInSeconds(textLength);
	const imageTime = estimateImageTimeInSeconds(imageCount);
	const audioTime = estimateAudioTimeInSeconds(audioLengthInSeconds);

	return readingTime + imageTime + audioTime + overheadBuffer;
}

export function getTimeFactorForItem(
	item: BaseItemSchema,
	actualTimeTaken: number,
	totalSlidesDuration: number,
) {
	const { content, currentStimulusItemIndex, stimulus } = item;
	const textLength = calculateTextLength(item);
	let imageCount = 0;
	imageCount = content?.media?.length ?? 0;
	const stimulusMedia =
		currentStimulusItemIndex === 1 ? (stimulus?.slides?.length ?? 0) : 0;
	const audioLengthInSeconds = stimulusMedia ? totalSlidesDuration : 0;
	if (stimulusMedia) {
		imageCount += stimulusMedia;
	}

	const expectedTime = calculateExpectedTime({
		textLength,
		imageCount,
		audioLengthInSeconds,
	});

	const normalizedTimeTaken = Math.min(expectedTime - actualTimeTaken, 2);

	return {
		timeFactor: calcTimeFactor(
			actualTimeTaken,
			expectedTime,
			/* scaleFactor */ 20,
		),
		normalizedTimeTaken,
	};
}
